import { FormEvent, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import * as yup from "yup";
import { FirebaseError } from "firebase/app";
import { ContentWrapper } from "../../components/ContentWrapper";
import { TopNav } from "../../components/TopNav";
import { PropositionTheme } from "../../utils/types";
import { findAllThemes } from "../../database/queries/queries";
import { addNewTheme } from "../../database/queries/commands";

interface FormState {
  name: string;
  color: string;
  imageUrl: string;
}

const emptyForm: FormState = { name: "", color: "#1976d2", imageUrl: "" };

type ValidationErrors = Partial<Record<keyof FormState, string>>;

function ThemeImagePreview({ imageUrl }: { imageUrl: string }) {
  const [hasImage, setHasImage] = useState(false);

  useEffect(() => {
    if (!imageUrl?.trim()) {
      setHasImage(false);
      return;
    }
    const img = new Image();
    img.onload = () => setHasImage(true);
    img.onerror = () => setHasImage(false);
    img.src = imageUrl;
  }, [imageUrl]);

  if (!hasImage) return null;

  return (
    <Box sx={{ marginTop: 1, display: "flex", justifyContent: "center" }}>
      <img
        src={imageUrl}
        alt="Pré-visualização do tema"
        style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain" }}
      />
    </Box>
  );
}

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("O nome é obrigatório")
    .min(2, "Mínimo de 2 caracteres")
    .max(50, "Máximo de 50 caracteres"),
  color: yup
    .string()
    .required("A cor é obrigatória")
    .matches(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Use um hex válido (ex.: #1976d2)"),
  imageUrl: yup.string().url("Informe uma URL válida").notRequired(),
});

export function ThemesApp() {
  const [themes, setThemes] = useState<PropositionTheme[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState>(emptyForm);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadThemes = async () => {
    setLoadError(null);
    try {
      const list = await findAllThemes();
      setThemes(list);
    } catch (err) {
      console.error(err);
      setLoadError("Não foi possível carregar os temas.");
    }
  };

  useEffect(() => {
    loadThemes();
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      await validationSchema.validate(
        {
          name: formState.name,
          color: formState.color,
          imageUrl: formState.imageUrl || undefined,
        },
        { abortEarly: false },
      );
      setValidationErrors({});
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const errors: ValidationErrors = {};
        err.inner.forEach((e) => {
          if (e.path) errors[e.path as keyof FormState] = e.message;
        });
        setValidationErrors(errors);
        return;
      }
      throw err;
    }

    setIsSaving(true);
    try {
      const id = await addNewTheme({
        name: formState.name.trim(),
        color: formState.color,
        imageUrl: formState.imageUrl.trim() || undefined,
      });
      setSubmitSuccess(`Tema criado com sucesso (id: ${id}).`);
      setFormState(emptyForm);
      await loadThemes();
    } catch (err) {
      console.error(err);
      if (err instanceof FirebaseError) {
        if (err.code === "permission-denied") {
          setSubmitError(
            "Permissão negada pelo Firestore. Verifique as regras da coleção themes.",
          );
        } else {
          setSubmitError(`${err.code}: ${err.message}`);
        }
      } else {
        setSubmitError("Não foi possível salvar o tema. Tente novamente.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box style={{ display: "flex", height: "100vh", flexDirection: "column" }}>
      <TopNav label="Gerenciar temas" />
      <ContentWrapper hasScroll>
        <Box sx={{ paddingX: 4, paddingBottom: 4 }}>
          <Paper elevation={1} sx={{ padding: 3, marginBottom: 3 }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Criar novo tema
            </Typography>
            <Box
              component="form"
              onSubmit={onSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              {submitError && <Alert severity="error">{submitError}</Alert>}
              {submitSuccess && (
                <Alert severity="success">{submitSuccess}</Alert>
              )}
              <FormControl fullWidth>
                <TextField
                  required
                  label="Nome"
                  error={Boolean(validationErrors.name)}
                  value={formState.name}
                  onChange={(e) =>
                    setFormState({ ...formState, name: e.target.value })
                  }
                  variant="outlined"
                />
                <FormHelperText>{validationErrors.name}</FormHelperText>
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  required
                  label="Cor (hex)"
                  type="color"
                  error={Boolean(validationErrors.color)}
                  value={formState.color}
                  onChange={(e) =>
                    setFormState({ ...formState, color: e.target.value })
                  }
                  variant="outlined"
                  sx={{ "& input": { height: 56, padding: 1 } }}
                />
                <FormHelperText>{validationErrors.color}</FormHelperText>
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  label="URL da imagem (opcional)"
                  error={Boolean(validationErrors.imageUrl)}
                  value={formState.imageUrl}
                  onChange={(e) =>
                    setFormState({ ...formState, imageUrl: e.target.value })
                  }
                  variant="outlined"
                />
                <FormHelperText>
                  {validationErrors.imageUrl ||
                    "Cole uma URL pública. Deixe em branco se não houver imagem."}
                </FormHelperText>
                <ThemeImagePreview imageUrl={formState.imageUrl} />
              </FormControl>
              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: 4,
                  paddingX: 3,
                  paddingY: 1,
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                {isSaving ? "Salvando…" : "Criar tema"}
              </Button>
            </Box>
          </Paper>

          <Paper elevation={1} sx={{ padding: 3 }}>
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
              Temas existentes
            </Typography>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            {!themes && !loadError && (
              <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {themes && themes.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nenhum tema cadastrado ainda.
              </Typography>
            )}
            {themes && themes.length > 0 && (
              <List>
                {themes.map((theme, idx) => (
                  <Box key={theme.id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem>
                      <ListItemAvatar>
                        {theme.imageUrl ? (
                          <Avatar src={theme.imageUrl} alt={theme.name} />
                        ) : (
                          <Avatar sx={{ bgcolor: theme.color }}>
                            {theme.name.slice(0, 1).toUpperCase()}
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={theme.name}
                        secondary={
                          <Chip
                            size="small"
                            label={theme.color}
                            sx={{
                              backgroundColor: theme.color,
                              color: "#fff",
                              fontFamily: "monospace",
                            }}
                          />
                        }
                      />
                    </ListItem>
                  </Box>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      </ContentWrapper>
    </Box>
  );
}

export default ThemesApp;
